import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import { PencilFill, CheckLg, XLg } from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/SecurityContext';
import RoleGuard from '../security/RoleGuard';
import SectionControls from '../SectionControls';
import { updateCtaBannerSection } from '../../services/contentService';
import './CtaBannerBlock.scss';

export function defaultCtaBannerData() {
  return {
    title: 'Ready to take on a challenge?',
    subtitle: 'Browse active tracks and submit your solution',
    buttonText: 'View challenge tracks',
    buttonLinkUrl: '/page/tracks',
    openInNewTab: false,
  };
}

// Read-only renderer. Red full-width strip with title + optional subtitle on
// the left and a pill button on the right. The whole block also acts as a
// click target on smaller screens where the button isn't easily reachable.
export function CtaBannerView({ data }) {
  const title = data?.title || '';
  const subtitle = data?.subtitle || '';
  const buttonText = data?.buttonText || '';
  const url = data?.buttonLinkUrl || '';
  const target = data?.openInNewTab ? '_blank' : '_self';
  const rel = data?.openInNewTab ? 'noopener noreferrer' : undefined;

  return (
    <div className="cta-banner-block">
      <div className="cta-banner-block__text">
        {title ? <div className="cta-banner-block__title">{title}</div> : null}
        {subtitle ? <div className="cta-banner-block__subtitle">{subtitle}</div> : null}
      </div>
      {buttonText ? (
        <a
          href={url || '#'}
          className="cta-banner-block__button"
          target={target}
          rel={rel}
        >
          {buttonText}
        </a>
      ) : null}
    </div>
  );
}

function CtaBannerBlock({
  pageId, section, onMoveUp, onMoveDown, onDelete, isFirstSection, isLastSection,
}) {
  const { user } = useAuth();
  const initialData = useMemo(() => section?.data || defaultCtaBannerData(), [section]);
  const [data, setData] = useState(initialData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setData(section?.data || defaultCtaBannerData());
  }, [section]);

  async function handleSave() {
    setIsSaving(true);
    try {
      const modifiedBy = user?.attributes?.email || user?.username || 'user';
      await updateCtaBannerSection(pageId, section.position, data, modifiedBy);
      setIsEditing(false);
    } catch (e) {
      console.error('Error saving CTA banner:', e);
      // eslint-disable-next-line no-alert
      alert('Failed to save section. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() {
    setData(section?.data || defaultCtaBannerData());
    setIsEditing(false);
  }

  function updateField(field, value) {
    setData((prev) => ({ ...prev, [field]: value }));
  }

  if (isEditing) {
    return (
      <div className="cta-banner-block cta-banner-block--editing">
        <Row className="g-2">
          <Col md={6}>
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={data.title || ''}
              onChange={(e) => updateField('title', e.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label>Subtitle (optional)</Form.Label>
            <Form.Control
              type="text"
              value={data.subtitle || ''}
              onChange={(e) => updateField('subtitle', e.target.value)}
            />
          </Col>
          <Col md={4}>
            <Form.Label>Button text</Form.Label>
            <Form.Control
              type="text"
              value={data.buttonText || ''}
              onChange={(e) => updateField('buttonText', e.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label>Button link URL</Form.Label>
            <Form.Control
              type="text"
              placeholder="/page/tracks or https://…"
              value={data.buttonLinkUrl || ''}
              onChange={(e) => updateField('buttonLinkUrl', e.target.value)}
            />
          </Col>
          <Col md={2} className="d-flex align-items-end">
            <Form.Check
              type="checkbox"
              id={`cta-newtab-${section?.position}`}
              label="New tab"
              checked={!!data.openInNewTab}
              onChange={(e) => updateField('openInNewTab', e.target.checked)}
            />
          </Col>
        </Row>

        <div className="cta-banner-block__editor-actions">
          <Button variant="success" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</>
            ) : (
              <><CheckLg /> Save</>
            )}
          </Button>
          <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
            <XLg /> Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="cta-banner-block-wrapper">
      <CtaBannerView data={data} />
      <RoleGuard role="admin">
        <div className="admin-controls">
          <Button
            variant="outline-secondary"
            size="sm"
            className="edit-btn"
            onClick={() => setIsEditing(true)}
          >
            <PencilFill /> Edit banner
          </Button>
          <SectionControls
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onDelete={onDelete}
            isFirstSection={isFirstSection}
            isLastSection={isLastSection}
          />
        </div>
      </RoleGuard>
    </div>
  );
}

export default CtaBannerBlock;
